'use client'

import React, { FC, ReactElement } from 'react'
import styles from './UtxosCart.module.css'
import classNames from 'classnames'
import Input from '@/components/contained-input'
import Button from '@/components/button'
import GradientButton from '@/components/button-gradient-epic'
import FeeSlider from '@/components/fee-slider'
import Wallet from '@/utils/wallet'
import axios from 'axios'
import { API_URL, NETWORK } from '@/utils/constants'
import Icon from '@/icons/lightning-orb'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import UtxoItem from '@/components/utxo-item'
import SelectUtxoItem from '../select-utxo-item'
import InfiniteScroll from 'react-infinite-scroll-component'
import Link from 'next/link'

import PlusIcon from '@/icons/plus-circle'
import MinusIcon from '@/icons/minus-circle'
import EditIcon from '@/icons/edit'

import Event from '@/utils/event'

import useDebounce from '@/utils/hooks/useDebounce'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

const LIMIT = 100
const CONSOLIDATION_LIMIT = 2000

const UtxosCart: FC<{
	handleModalClose: () => void
}> = (props): ReactElement => {
	const { data } = useSwr(`${API_URL}/wallet/${Wallet.balanceAddress()}/all-utxos`, fetcher)

	const { handleModalClose } = props

	const [step, setStep] = React.useState(1)
	const [selected, setSelected] = React.useState(new Set())
	const [numOutputs, setOutputs] = React.useState(2)
	const [size, setSize] = React.useState<any>(null)
	const [feeRate, setFeeRate] = React.useState(0)
	const [fee, setFee] = React.useState(0)
	const [loading, setLoading] = React.useState(false)
	const debouncedNumOuputs: number = useDebounce<number>(numOutputs, 300)
	const debouncedFeeRate: number = useDebounce<number>(feeRate, 300)
	const { exchangeRate } = useExchangeRate()
	const [sendToAddress, setSendToAddress] = React.useState('')

	const utxos = React.useMemo(() => {
		return (data?.utxos || [])
			?.sort((a: any, b: any) => b.value - a.value)
			.map((e: any) => ({ ...e, outpoint: `${e.txid}:${e.vout}` }))
	}, [data])

	const { selectedUtxos, inputSats } = React.useMemo(() => {
		const selectedUtxos = Array.from(selected?.values() || []).map((e) =>
			utxos.find((u: any) => u.outpoint === e)
		)
		const inputSats = selectedUtxos.reduce((a, e) => e.value + a, 0)

		return { selectedUtxos, inputSats }
	}, [selected, utxos])

	const handleSelect = React.useCallback(
		(outpoint: any) => {
			if (selected.has(outpoint)) {
				const newSet = new Set(selected)
				newSet.delete(outpoint)

				setSelected(newSet)
			} else {
				setSelected(new Set(selected).add(outpoint))
			}
		},
		[selected]
	)

	const selectAboveNumberSats = (number: any) => {
		const newSet = new Set()
		if (utxos && utxos?.length > 0) {
			utxos.forEach((utxo: any) => {
				if (
					!selected.has(utxo?.outpoint) &&
					utxo.value > number &&
					newSet.size < CONSOLIDATION_LIMIT
				) {
					newSet.add(utxo.outpoint)
				}
			})
		}

		setSelected(newSet)
	}

	const selectBelowNumberSats = (number: any) => {
		const newSet = new Set()
		if (utxos && utxos?.length > 0) {
			utxos.forEach((utxo: any) => {
				if (
					!selected.has(utxo?.outpoint) &&
					utxo.value < number &&
					newSet.size < CONSOLIDATION_LIMIT
				) {
					newSet.add(utxo.outpoint)
				}
			})
		}

		setSelected(newSet)
	}

	React.useEffect(() => {
		if (!selectedUtxos.length) {
			let utxo = utxos[0]

			let set = new Set()

			if (utxo) {
				requestAnimationFrame(() => {
					setSelected(new Set(set).add(utxo.outpoint))
				})
			}
		}
	}, [selectedUtxos.length, utxos])

	React.useEffect(() => {
		async function run() {
			setLoading(true)
			try {
				if (!selectedUtxos.length) {
					return
				}

				// @ts-ignore
				const outputs = new Array(parseInt(debouncedNumOuputs, 10))
					.fill('')
					.map((_e) => [Wallet.balanceAddress(), NETWORK === 'BTC' ? 546 : 1e6])

				const payload = {
					outputs: outputs,
					public_key: Wallet.getPublicKey(),
					from: Wallet.balanceAddress(),
					utxos: selectedUtxos.map((e) => [e.txid, e.vout, e.value]),
					fee_rate: debouncedFeeRate,
					purpose: 'utxo_split'
				}

				const { data } = await axios.post(`${API_URL}/wallet/build`, payload)

				console.log({ inputSats, data })

				const value = parseInt(
					Math.floor((inputSats - data.fees) / debouncedNumOuputs).toFixed(0),
					10
				)

				setFee(data.fees)
				setSize(value as any)
			} catch (e) {
				console.log(e)
			}

			setLoading(false)
		}

		run()
	}, [debouncedNumOuputs, debouncedFeeRate, selectedUtxos, inputSats])

	const handleSubmit = React.useCallback(async () => {
		// @ts-ignore
		const outputs = new Array(parseInt(numOutputs, 10))
			.fill('')
			.map((_e) => [sendToAddress || Wallet.balanceAddress(), size])

		const payload = {
			outputs: outputs,
			public_key: Wallet.getPublicKey(),
			from: Wallet.balanceAddress(),
			utxos: selectedUtxos.map((e) => [e.txid, e.vout, e.value]),
			fee_rate: feeRate,
			purpose: 'utxo_split'
		}

		const { data } = await axios.post(`${API_URL}/wallet/build`, payload)

		const { rawtx } = await Wallet.signInscribe(data.psbt)
		handleModalClose()

		Event.emit('open-broadcast-modal', {
			type: 'send',
			address: sendToAddress || Wallet.balanceAddress(),
			rawtx,
			psbt: data.psbt
		})
	}, [numOutputs, selectedUtxos, feeRate, handleModalClose, sendToAddress, size])

	const renderInput = React.useCallback((e: any) => <UtxoItem key={e.outpoint} utxo={e} />, [])

	const renderOutput = React.useCallback(
		(e: any, i: any) => {
			const utxo = {
				outpoint: '',
				txid: '',
				vout: 0,
				value: size,
				status: {
					confirmed: false
				}
			}

			return <UtxoItem key={i} utxo={utxo} />
		},
		[size]
	)

	const handleChange = React.useCallback(async (evt: any) => {
		setSendToAddress(evt.target.value)
	}, [])

	const renderSelectUtxo = React.useCallback(
		(e: any) => (
			<SelectUtxoItem
				onSelect={handleSelect}
				selected={selected.has(e.outpoint)}
				key={e.outpoint}
				utxo={e}
			/>
		),
		[selected, handleSelect]
	)

	const [page, setPage] = React.useState(0)
	const fetchMore = React.useCallback(async () => setPage((prev) => prev + 1), [])
	const viewData = React.useMemo(() => utxos.slice(0, (1 + page) * LIMIT), [page, utxos])

	if (step === 1) {
		return (
			<div className={styles.root}>
				<Icon className={styles.icon} />
				<p className={classNames(styles.title, 'text-lg', 'semibold')}>
					Select single or multiple UTXOs to split
				</p>
				<p className={classNames(styles.description, 'text-sm', 'regular')}>
					Splitting your UTXOs will allow you to make more purchases at once. For example, if you
					have a single $1,000 UTXO, split it into ten $100 UTXOs to make ten $100 purchases in a
					row.
				</p>
				<div className={styles.selectContainer}>
					<div
						className={styles.selectAboveRow}
						onClick={() => selectAboveNumberSats(10000)}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => selectAboveNumberSats(10000))}
					>
						<img src="/white-check-circle.svg" alt="" className={styles.checkIcon} />
						<div className={styles.selectAboveText}>Select all above 10,000 sats</div>
					</div>
					<div
						className={styles.selectBelowRow}
						onClick={() => selectBelowNumberSats(10000)}
						role="button"
						tabIndex={0}
						onKeyDown={onKeyboardClick(() => selectBelowNumberSats(10000))}
					>
						<img src="/white-check-circle.svg" alt="" className={styles.checkIcon} />
						<div className={styles.selectAboveText}>Select all below 10,000 sats</div>
					</div>
				</div>
				<div id="utxo-scroll" className={styles.container}>
					<InfiniteScroll
						className={styles.infiniteScroll}
						dataLength={viewData?.length}
						next={fetchMore}
						hasMore={viewData?.length % LIMIT === 0}
						loader={<div className={styles.loading}></div>}
						scrollableTarget="utxo-scroll"
					>
						{viewData?.map(renderSelectUtxo)}
					</InfiniteScroll>
				</div>
				<GradientButton
					className={styles.submitButton}
					disabled={!selectedUtxos.length}
					onClick={() => (setStep(0))}
				>
					Select {selectedUtxos.length}
				</GradientButton>
				<Button background="transparent" className={styles.cancelButton} onClick={handleModalClose}>
					Cancel
				</Button>
			</div>
		)
	}

	return (
		<div className={styles.root}>
			<Icon className={styles.icon} />
			<p className={classNames(styles.title, 'text-lg', 'semibold')}>Create UTXOs</p>
			<p className={classNames(styles.description, 'text-sm', 'regular')}>
				Your inputs will create multiple outputs. Add outputs to split transactions at the desired
				UTXO size.
			</p>
			<div className={styles.container}>
				<div className={classNames(styles.containerTitle, 'text-lg', 'semibold')}>
					Selected Inputs
					<div className={styles.containerActions}>
						<EditIcon
							className={styles.containerActionIcon}
							onClick={() => (setStep(1))}
						/>
					</div>
				</div>
				{selectedUtxos?.map(renderInput)}
			</div>
			<div className={styles.container}>
				<div className={classNames(styles.containerTitle, 'text-lg', 'semibold')}>
					Outputs: {numOutputs}
					<div className={styles.containerActions}>
						<MinusIcon
							className={styles.containerActionIcon}
							onClick={() => setOutputs(Math.max(1, numOutputs - 1))}
						/>
						<PlusIcon
							className={styles.containerActionIcon}
							onClick={() => setOutputs(numOutputs + 1)}
						/>
					</div>
				</div>
				{new Array(numOutputs).fill('').map(renderOutput)}
			</div>
			<div className={styles.networkContainer}>
				<FeeSlider feeRate={feeRate} setFeeRate={setFeeRate} />
				<div className={styles.networkBody}>
					<p className={classNames(styles.networkTitle, 'text-sm', 'bold')}>Total Fee cost</p>
					<div className={styles.networkAmount}>
						<p className={classNames('text-xs', 'bold', styles.btc)}>
							{(fee / 1e8).toFixed(8)} {NETWORK}
						</p>
						<p className={classNames('text-sm', 'regular', styles.usd)}>
							~${((fee / 1e8) * exchangeRate).toFixed(2)}
						</p>
					</div>
				</div>
			</div>
			<div className={styles.addressAndDescriptionContainer}>
				<Input
					className={styles.sendAddressContainer}
					inputClassName={classNames(styles.sendAddressInput)}
					placeholder="Send to address"
					value={sendToAddress}
					inputId={'myInput'}
					onChange={handleChange}
				/>
				<div className={styles.sendAddressDescription}>
					If address is not specified we will send to your connected wallet.
				</div>
			</div>
			<div className="items-center text-muted-foreground text-xs pt-2 pb-1">
				By clicking "Create UTXOs", you agree to the Ordinals Wallet{' '}
				<Link href={`/terms`} className="underline" target="_blank">
					Terms of Service.
				</Link>
			</div>
			<GradientButton className={styles.submitButton} disabled={loading} onClick={handleSubmit}>
				Create UTXOs
			</GradientButton>
			<Button
				background="transparent"
				className={styles.cancelButton}
				onClick={() => (setStep(1))}
			>
				Go Back
			</Button>
		</div>
	)
}

export default UtxosCart
